import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, PaperPlaneTilt, Storefront, CurrencyInr, ShieldSlash, ShieldCheck, Gift, PencilSimple, X, CheckCircle, Warning, CaretDown, MapPin, UserPlus, Trash } from "@phosphor-icons/react";
import { INDIA_STATES, CITIES_BY_STATE, DEFAULT_STATE, DEFAULT_CITY } from "@/lib/indiaGeo";
import IndiaMapDashboard from "./IndiaMapDashboard";

const TYPES = [
  { v: "retail", l: "Retail / Shop" },
  { v: "grocery", l: "Grocery" },
  { v: "salon", l: "Salon / Beauty" },
  { v: "clinic", l: "Clinic / Doctor" },
  { v: "cafe", l: "Cafe / Restaurant" },
  { v: "services", l: "Services" },
  { v: "other", l: "Other" },
];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [regionalAdmins, setRegionalAdmins] = useState([]);
  const [regions, setRegions] = useState([]);
  const [showRAModal, setShowRAModal] = useState(false);
  const [newRA, setNewRA] = useState({ name: "", email: "", password: "", region_slug: "pune" });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const isSuper = stats?.scope !== "region";

  const load = async () => {
    setLoading(true);
    const [s, v] = await Promise.all([api.get("/admin/stats"), api.get("/admin/vendors")]);
    setStats(s.data); setVendors(v.data);
    // Only super admin can see regional admins + regions
    if (s.data.scope !== "region") {
      try {
        const [ra, rg] = await Promise.all([
          api.get("/admin/regional-admins"),
          api.get("/public/regions"),
        ]);
        setRegionalAdmins(ra.data);
        setRegions(rg.data);
      } catch (e) { /* not super admin — silently skip */ }
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createRA = async () => {
    if (!newRA.email || !newRA.password || !newRA.name) {
      toast.error("Please fill name, email, password"); return;
    }
    try {
      await api.post("/admin/regional-admins", newRA);
      toast.success(`Regional admin created for ${newRA.region_slug}`);
      setShowRAModal(false);
      setNewRA({ name: "", email: "", password: "", region_slug: "pune" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create regional admin");
    }
  };
  const deleteRA = async (ra) => {
    if (!window.confirm(`Delete regional admin ${ra.email}?`)) return;
    try {
      await api.delete(`/admin/regional-admins/${ra.id}`);
      toast.success("Regional admin removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggle = async (v) => {
    const path = v.is_suspended ? "unsuspend" : "suspend";
    await api.post(`/admin/vendors/${v.id}/${path}`);
    toast.success(v.is_suspended ? "Vendor reactivated" : "Vendor suspended");
    load();
  };
  const grant = async (v) => {
    if (!window.confirm(`Grant ${v.business_name} 30 days of free subscription?`)) return;
    await api.post(`/admin/vendors/${v.id}/grant`);
    toast.success("30 days granted");
    load();
  };
  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/admin/vendors/${editing.id}`, {
        name: editing.name.trim(),
        business_name: editing.business_name.trim(),
        business_type: editing.business_type,
        phone: (editing.phone || "").trim() || null,
        email: editing.email.trim(),
      });
      toast.success("Vendor updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Owner console</div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Admin</h1>
        <p className="text-brand-text/70 mt-1">Monitor and edit every vendor across Vendorly.</p>
      </div>

      {stats?.scope === "region" && stats?.region_slug && (
        <div className="mb-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand-primary bg-brand-primary/10 rounded-full px-3 py-1">
          <MapPin size={12} weight="duotone" /> Scoped to {stats.region_slug}
        </div>
      )}

      <div className="mb-8">
        <IndiaMapDashboard />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Stat icon={Storefront} label="Total vendors" value={stats?.total_vendors ?? "—"} testid="a-stat-vendors" />
        <Stat icon={ShieldCheck} label="Active vendors" value={stats?.active_vendors ?? "—"} testid="a-stat-active" />
        <Stat icon={Users} label="Total customers" value={stats?.total_customers ?? "—"} testid="a-stat-customers" />
        <Stat icon={PaperPlaneTilt} label="Total messages" value={stats?.total_messages ?? "—"} testid="a-stat-messages" />
        <Stat icon={CurrencyInr} label="Revenue" value={stats ? `₹${stats.revenue_inr}` : "—"} testid="a-stat-revenue" />
      </div>

      <div className="bg-white border border-brand-border rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-brand-border">
          <h3 className="font-heading text-xl font-bold">All vendors</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg border-b border-brand-border text-left">
              <tr>
                <th className="p-4 font-heading font-semibold">Business</th>
                <th className="p-4 font-heading font-semibold hidden md:table-cell">Email</th>
                <th className="p-4 font-heading font-semibold">Type</th>
                <th className="p-4 font-heading font-semibold">Sub</th>
                <th className="p-4 font-heading font-semibold">Customers</th>
                <th className="p-4 font-heading font-semibold hidden lg:table-cell">Messages</th>
                <th className="p-4 font-heading font-semibold hidden lg:table-cell">Joined</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-10 text-center text-brand-textMuted">Loading…</td></tr>
              ) : vendors.length === 0 ? (
                <tr><td colSpan={8} className="p-12 text-center text-brand-textMuted">No vendors have signed up yet.</td></tr>
              ) : vendors.map(v => {
                const expiring = v.subscription_expires_at ? new Date(v.subscription_expires_at) : null;
                const active = expiring && expiring > new Date();
                return (
                  <tr key={v.id} className="border-b border-brand-border" data-testid={`admin-row-${v.id}`}>
                    <td className="p-4">
                      <div className="font-semibold">{v.business_name}</div>
                      <div className="text-xs text-brand-textMuted">{v.name}</div>
                    </td>
                    <td className="p-4 hidden md:table-cell text-brand-text/80">
                      <div className="flex items-center gap-1.5">
                        <span>{v.email}</span>
                        {v.email_verified ? (
                          <CheckCircle size={14} weight="fill" className="text-brand-success" title="Verified" data-testid={`verified-${v.id}`} />
                        ) : (
                          <Warning size={14} weight="fill" className="text-brand-warning" title="Not verified" data-testid={`unverified-${v.id}`} />
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-brand-text/80 capitalize">
                      <div>{v.business_type}</div>
                      {(v.city || v.state) && (
                        <div className="text-[11px] text-brand-textMuted normal-case mt-0.5" data-testid={`loc-${v.id}`}>
                          {v.city}, {v.state}
                        </div>
                      )}
                      {v.region_slug && (
                        <div className="text-[10px] uppercase tracking-widest text-brand-primary/70 normal-case mt-1" data-testid={`region-${v.id}`}>
                          {v.region_slug}
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      {v.is_suspended ? (
                        <span className="text-xs font-semibold bg-brand-error/15 text-brand-error px-2 py-1 rounded-full">Suspended</span>
                      ) : active ? (
                        <span className="text-xs font-semibold bg-brand-success/15 text-brand-success px-2 py-1 rounded-full">Active</span>
                      ) : (
                        <span className="text-xs font-semibold bg-brand-warning/15 text-brand-warning px-2 py-1 rounded-full">Pending</span>
                      )}
                      {expiring && active && <div className="text-[11px] text-brand-textMuted mt-1">until {expiring.toLocaleDateString()}</div>}
                    </td>
                    <td className="p-4">{v.customers_count}</td>
                    <td className="p-4 hidden lg:table-cell">{v.messages_count}</td>
                    <td className="p-4 hidden lg:table-cell text-brand-textMuted">{new Date(v.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={()=>setEditing({...v})} title="Edit details" data-testid={`edit-${v.id}`}
                          className="p-2 rounded-lg hover:bg-brand-bg text-brand-primary">
                          <PencilSimple size={16} weight="duotone" />
                        </button>
                        <button onClick={()=>grant(v)} title="Grant 30 days" data-testid={`grant-${v.id}`}
                          className="p-2 rounded-lg hover:bg-brand-bg text-brand-secondary">
                          <Gift size={16} weight="duotone" />
                        </button>
                        <button onClick={()=>toggle(v)} data-testid={`toggle-${v.id}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold ${v.is_suspended ? "bg-brand-success text-white hover:opacity-90" : "border border-brand-error text-brand-error hover:bg-brand-error/10"}`}>
                          {v.is_suspended ? <><ShieldCheck size={14} weight="duotone" className="inline mr-1" />Reactivate</> : <><ShieldSlash size={14} weight="duotone" className="inline mr-1" />Suspend</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isSuper && (
        <div className="mt-8 bg-white border border-brand-border rounded-2xl overflow-hidden" data-testid="regional-admins-section">
          <div className="p-5 border-b border-brand-border flex items-center justify-between">
            <div>
              <h3 className="font-heading text-xl font-bold">Regional Admins</h3>
              <p className="text-sm text-brand-textMuted mt-1">Delegate operations for a region. They only see their region's data.</p>
            </div>
            <button onClick={()=>setShowRAModal(true)} data-testid="add-ra-btn"
              className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-4 py-2 text-sm font-semibold">
              <UserPlus size={16} weight="duotone" /> Add regional admin
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-bg border-b border-brand-border text-left">
                <tr>
                  <th className="p-4 font-heading font-semibold">Name</th>
                  <th className="p-4 font-heading font-semibold">Email</th>
                  <th className="p-4 font-heading font-semibold">Region</th>
                  <th className="p-4 font-heading font-semibold">Joined</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {regionalAdmins.length === 0 ? (
                  <tr><td colSpan={5} className="p-10 text-center text-brand-textMuted">No regional admins yet. Add one to delegate a region.</td></tr>
                ) : regionalAdmins.map(ra => (
                  <tr key={ra.id} className="border-b border-brand-border" data-testid={`ra-row-${ra.id}`}>
                    <td className="p-4 font-semibold">{ra.name || "—"}</td>
                    <td className="p-4 text-brand-text/80">{ra.email}</td>
                    <td className="p-4"><span className="text-xs uppercase tracking-widest bg-brand-primary/10 text-brand-primary px-2 py-1 rounded-full">{ra.region_slug}</span></td>
                    <td className="p-4 text-brand-textMuted">{new Date(ra.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-right">
                      <button onClick={()=>deleteRA(ra)} data-testid={`delete-ra-${ra.id}`}
                        className="p-2 rounded-lg hover:bg-brand-error/10 text-brand-error"><Trash size={16} weight="duotone" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showRAModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ra-modal">
          <div className="bg-white w-full max-w-md rounded-3xl border border-brand-border p-6 sm:p-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-2xl font-bold">Add regional admin</h3>
              <button onClick={()=>setShowRAModal(false)}><X size={22} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Name">
                <input value={newRA.name} onChange={(e)=>setNewRA({...newRA, name: e.target.value})} data-testid="ra-name"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border outline-none" placeholder="Amit Sharma" />
              </Field>
              <Field label="Email">
                <input type="email" value={newRA.email} onChange={(e)=>setNewRA({...newRA, email: e.target.value})} data-testid="ra-email"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border outline-none" placeholder="pune@vendorly.in" />
              </Field>
              <Field label="Temp password">
                <input type="text" value={newRA.password} onChange={(e)=>setNewRA({...newRA, password: e.target.value})} data-testid="ra-password"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border outline-none" placeholder="min 6 chars — they'll change on first login" />
              </Field>
              <Field label="Region">
                <select value={newRA.region_slug} onChange={(e)=>setNewRA({...newRA, region_slug: e.target.value})} data-testid="ra-region"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border">
                  {regions.map(r => <option key={r.slug} value={r.slug}>{r.name} — {r.state}</option>)}
                </select>
              </Field>
            </div>
            <div className="mt-7 flex gap-3">
              <button onClick={()=>setShowRAModal(false)} className="flex-1 h-11 rounded-full border border-brand-border hover:bg-brand-bg font-semibold">Cancel</button>
              <button onClick={createRA} data-testid="ra-create-btn"
                className="flex-1 h-11 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold">Create</button>
            </div>
          </div>
        </div>
      )}

      {editing && (        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 overflow-y-auto" data-testid="edit-vendor-modal">
          <div className="bg-white w-full max-w-lg rounded-3xl border border-brand-border p-6 sm:p-8 my-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-2xl font-bold">Edit vendor</h3>
              <button onClick={()=>setEditing(null)} data-testid="close-edit-vendor"><X size={22} /></button>
            </div>
            <div className="space-y-4">
              <Field label="Business name">
                <input value={editing.business_name} onChange={(e)=>setEditing({...editing, business_name: e.target.value})} data-testid="e-business-name"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
              </Field>
              <Field label="Business type">
                <select value={editing.business_type} onChange={(e)=>setEditing({...editing, business_type: e.target.value})} data-testid="e-business-type"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border">
                  {TYPES.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </Field>
              <Field label="Owner name">
                <input value={editing.name} onChange={(e)=>setEditing({...editing, name: e.target.value})} data-testid="e-name"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
              </Field>
              <Field label="Email">
                <input value={editing.email} onChange={(e)=>setEditing({...editing, email: e.target.value})} data-testid="e-email"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
                <p className="mt-1 text-xs text-brand-textMuted">Changing email will mark it as unverified.</p>
              </Field>
              <Field label="Phone">
                <input value={editing.phone || ""} onChange={(e)=>setEditing({...editing, phone: e.target.value})} data-testid="e-phone" placeholder="+91 98765 43210"
                  className="w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="State">
                  <div className="relative">
                    <select value={editing.state || DEFAULT_STATE}
                      onChange={(e)=> {
                        const v = e.target.value;
                        const cities = CITIES_BY_STATE[v] || [];
                        setEditing({...editing, state: v, city: cities[0] || DEFAULT_CITY});
                      }}
                      data-testid="e-state"
                      className="w-full h-11 pl-4 pr-10 rounded-xl bg-brand-bg border border-brand-border appearance-none">
                      {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-textMuted pointer-events-none" />
                  </div>
                </Field>
                <Field label="City">
                  <div className="relative">
                    <select value={editing.city || DEFAULT_CITY}
                      onChange={(e)=>setEditing({...editing, city: e.target.value})}
                      data-testid="e-city"
                      className="w-full h-11 pl-4 pr-10 rounded-xl bg-brand-bg border border-brand-border appearance-none">
                      {(CITIES_BY_STATE[editing.state || DEFAULT_STATE] || [DEFAULT_CITY]).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-textMuted pointer-events-none" />
                  </div>
                </Field>
              </div>
              <div className="text-xs text-brand-textMuted">Slug (<code className="font-mono">{editing.slug}</code>) can't be changed — customers already have QR codes with this URL.</div>
            </div>
            <div className="mt-7 flex gap-3">
              <button onClick={()=>setEditing(null)} className="flex-1 h-11 rounded-full border border-brand-border hover:bg-brand-bg font-semibold" data-testid="cancel-edit-vendor">Cancel</button>
              <button onClick={saveEdit} disabled={saving} data-testid="save-edit-vendor"
                className="flex-1 h-11 rounded-full bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-semibold">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-brand-textMuted">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-textMuted">{label}</div>
        <Icon size={20} weight="duotone" className="text-brand-primary" />
      </div>
      <div className="mt-3 font-heading text-3xl font-bold tracking-tight">{value}</div>
    </div>
  );
}
